/**
 * ContentCompletionMeter — compact circular progress indicator
 * showing content customization progress in the edit toolbar.
 */

import { useContentEditingSafe } from "./ContentEditingContext";

function getColor(pct: number): string {
  if (pct >= 80) return "#22c55e"; // green-500
  if (pct >= 50) return "#eab308"; // yellow-500
  if (pct >= 25) return "#f97316"; // orange-500
  return "#ef4444"; // red-500
}

function getLabel(pct: number): string {
  if (pct >= 100) return "\u05D4\u05D5\u05E9\u05DC\u05DD!";
  if (pct >= 80) return "\u05DB\u05DE\u05E2\u05D8 \u05DE\u05D5\u05E9\u05DC\u05DD";
  if (pct >= 50) return "\u05D9\u05E4\u05D4";
  if (pct >= 25) return "\u05D7\u05DC\u05E7\u05D9";
  return "\u05D1\u05D4\u05EA\u05D7\u05DC\u05D4";
}

export function ContentCompletionMeter() {
  const ctx = useContentEditingSafe();
  if (!ctx || !ctx.isEditMode) return null;

  const { contentStats } = ctx;
  const { total, filled, percentage } = contentStats;

  // Don't show if no registered keys yet
  if (total === 0) return null;

  const clampedPct = Math.min(percentage, 100);
  const color = getColor(clampedPct);
  const label = getLabel(clampedPct);

  // SVG circular progress
  const size = 28;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clampedPct / 100) * circumference;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-white/10 text-xs cursor-default select-none"
      title={`${label}: ${filled}/${total} \u05E9\u05D3\u05D5\u05EA \u05EA\u05D5\u05DB\u05DF \u05D4\u05D5\u05EA\u05D0\u05DE\u05D5 (${clampedPct}%)`}
      dir="rtl"
    >
      <svg width={size} height={size} className="shrink-0 -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <span className="text-white" style={{ fontWeight: 600 }}>
        {clampedPct}%
      </span>
      <span className="hidden lg:inline text-white/70">
        ({filled}/{total})
      </span>
    </span>
  );
}
